import React from "react";

import {
  Activity,
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronUp,
  Clock3,
  Copy,
  ExternalLink,
  Eye,
  FileText,
  FlaskConical,
  Info,
  Layers3,
  Lock,
  MessageSquareText,
  Pause,
  PenTool,
  Play,
  Square,
  Tag,
  Timer,
  Users,
} from "lucide-react";

import { getRuntimeConfig } from "../../../../../../config/runtimeConfig";
import { INNOVATION_ROUTES } from "../../../../routes";
import {
  getExperimentApiErrorMessage,
  resolvePrototypeIterationIdFromDetail,
  usePrototypeByExperiment,
  useUpdateExperimentStatus,
} from "../../api/experimentsApi";
import {
  getFeedbackFlowErrorMessage,
  useFeedbackFlow,
} from "../../api/feedbackFlowApi";
import { buildExperimentParticipantPreviewUrl } from "../../config/previewUrl";
import { useExperimentEditor } from "../../hooks/useExperimentEditor";
import {
  getCreditCardDemoFinalize,
  isDemoCreditCardExperimentId,
} from "../../utils/demoExperimentData";
import { formatExperimentStatusLabel } from "../../utils/experimentStatus";
import {
  getQuestionsBySection,
  isTaggedFeedbackQuestion,
} from "../../utils/feedbackFlow";

import type {
  ExperimentFormData,
  FeedbackFlowQuestion,
} from "../../types/experiment.types";

type ReviewSectionKey = "business" | "research" | "prototype";

interface ReviewAccordionCardProps {
  title: string;
  summary: string;
  icon: React.ReactNode;
  sectionKey: ReviewSectionKey;
  status: "complete" | "attention";
  isOpen: boolean;
  onToggle: () => void;
  actionSlot?: React.ReactNode;
  children: React.ReactNode;
}

interface ReviewFieldProps {
  label: string;
  value?: React.ReactNode;
  span?: "half" | "full";
}

interface ExperimentFinalizeSectionProps {
  experimentData: ExperimentFormData;
  goToStep: (step: number) => void;
  isFinalized: boolean;
  setIsFinalized: (value: boolean) => void;
}

const CARD_STYLES: Record<
  ReviewSectionKey,
  {
    borderClassName: string;
    iconClassName: string;
  }
> = {
  business: {
    borderClassName: "border-sky-500/30",
    iconClassName: "bg-emerald-500/15 text-emerald-300",
  },
  research: {
    borderClassName: "border-emerald-500/25",
    iconClassName: "bg-emerald-500/15 text-emerald-300",
  },
  prototype: {
    borderClassName: "border-amber-500/30",
    iconClassName: "bg-amber-400/15 text-amber-300",
  },
};

const EXPERIMENT_TYPE_LABELS = {
  discovery: "Discovery",
  validation: "Validation",
  optimization: "Optimization",
} as const;

const PARAMETER_DEFAULTS = {
  populationSize: 103,
  minimumResponses: 20,
  targetResponses: 300,
  confidence: 95,
};

const RESPONSE_RATE_COLUMNS = [
  [5, 15, 30],
  [10, 20, 50],
] as const;

const DEPLOYMENT_STATE_STORAGE_KEY =
  "falcon-innovation-studio:deployment-state";

const PARTICIPANT_RESPONSE_ROWS = [
  {
    id: 1,
    firstName: "Sarah",
    lastName: "Chen",
    email: "sarah.chen@gmail.com",
    referral: "Social media",
    dateSubmitted: "07/10/25 9:00 AM",
  },
  {
    id: 2,
    firstName: "Marcus",
    lastName: "Williams",
    email: "marcus.w@outlook.com",
    referral: "Email invite",
    dateSubmitted: "07/10/25 1:00 PM",
  },
  {
    id: 3,
    firstName: "Aisha",
    lastName: "Patel",
    email: "aisha.patel@yahoo.com",
    referral: "Direct link",
    dateSubmitted: "07/10/25 5:00 PM",
  },
  {
    id: 4,
    firstName: "James",
    lastName: "Rodriguez",
    email: "j.rodriguez@gmail.com",
    referral: "Other",
    dateSubmitted: "07/10/25 9:00 PM",
  },
  {
    id: 5,
    firstName: "Emily",
    lastName: "Nakamura",
    email: "emily.n@hotmail.com",
    referral: "Social media",
    dateSubmitted: "07/11/25 1:00 AM",
  },
  {
    id: 6,
    firstName: "David",
    lastName: "Okafor",
    email: "david.okafor@gmail.com",
    referral: "Email invite",
    dateSubmitted: "07/11/25 5:00 AM",
  },
  {
    id: 7,
    firstName: "Priya",
    lastName: "Sharma",
    email: "priya.sharma@outlook.com",
    referral: "Direct link",
    dateSubmitted: "07/11/25 9:00 AM",
  },
  {
    id: 8,
    firstName: "Tyler",
    lastName: "Brooks",
    email: "tyler.brooks@gmail.com",
    referral: "Social media",
    dateSubmitted: "07/11/25 1:00 PM",
  },
] as const;

const DEMOGRAPHIC_RESPONSE_ROWS = [
  {
    id: "age-range",
    question: "What is your age range?",
    leadingAnswer: "25-34",
    completion: "8/8 completed",
  },
  {
    id: "income",
    question: "What is your annual household income?",
    leadingAnswer: "$75k-$100k",
    completion: "8/8 completed",
  },
  {
    id: "employment",
    question: "What is your employment status?",
    leadingAnswer: "Full-time employed",
    completion: "8/8 completed",
  },
] as const;

const INTERVIEW_RESPONSE_ROWS = [
  {
    id: 1,
    participant: "Sarah Chen",
    screen: "Screen 1",
    question: "Which credit card goal best matches your current financial priorities?",
    answer: "Travel rewards with low annual fees.",
  },
  {
    id: 2,
    participant: "Marcus Williams",
    screen: "Screen 1",
    question: "Was there a goal option you were looking for but couldn't find?",
    answer: "I wanted a clear cashback-focused option.",
  },
  {
    id: 3,
    participant: "Aisha Patel",
    screen: "Screen 1",
    question: "How confident do you feel about your credit card selection?",
    answer: "Fairly confident after comparing the options.",
  },
  {
    id: 4,
    participant: "James Rodriguez",
    screen: "Screen 2",
    question: "How comfortable did you feel providing your personal information?",
    answer: "Okay overall, but I paused at the income field.",
  },
  {
    id: 5,
    participant: "Emily Nakamura",
    screen: "Screen 2",
    question: "What would make you feel more secure sharing sensitive information?",
    answer: "A stronger explanation of why the data is needed.",
  },
  {
    id: 6,
    participant: "David Okafor",
    screen: "Screen 2",
    question: "Which fields felt most intrusive to complete?",
    answer: "Income and employment details.",
  },
  {
    id: 7,
    participant: "Priya Sharma",
    screen: "Screen 3",
    question: "How satisfied are you with the credit limit offered?",
    answer: "It felt lower than expected for my profile.",
  },
  {
    id: 8,
    participant: "Tyler Brooks",
    screen: "Screen 3",
    question: "How appealing is the sign-up bonus to you?",
    answer: "Strong motivator if the spending threshold is realistic.",
  },
  {
    id: 9,
    participant: "Sarah Chen",
    screen: "Screen 3",
    question: "Any concerns about the APR or terms presented?",
    answer: "The APR details were hard to compare quickly.",
  },
  {
    id: 10,
    participant: "Marcus Williams",
    screen: "Screen 4",
    question: "How easy was it to understand the terms and conditions?",
    answer: "Mostly clear, but a shorter summary would help.",
  },
  {
    id: 11,
    participant: "Aisha Patel",
    screen: "Screen 4",
    question: "Which aspects of the terms concerned you most?",
    answer: "Penalty fees and changing variable APRs.",
  },
  {
    id: 12,
    participant: "James Rodriguez",
    screen: "Screen 4",
    question: "What would help you better understand the terms?",
    answer: "A plain-language recap with examples.",
  },
] as const;

const buildAppUrl = (path: string) => `${window.location.origin}${path}`;
const buildDeploymentStateStorageKey = (experimentId: string) =>
  `${DEPLOYMENT_STATE_STORAGE_KEY}:${experimentId}`;

const formatNumber = (value: number) =>
  new Intl.NumberFormat("en-US").format(value);

const trimValue = (value?: string | null) => value?.trim() ?? "";

const sanitizeIntegerInput = (value: string) => value.replace(/[^\d]/g, "");

const formatDeploymentTimestamp = (value?: string | null) => {
  if (!value) {
    return "Not locked yet";
  }

  const date = new Date(value);
  return `${date.toLocaleDateString("en-GB")}, ${date.toLocaleTimeString(
    "en-GB",
    {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    },
  )}`;
};

const formatShortStartedDate = (value?: string | null) => {
  if (!value) {
    return "Started recently";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
  }).format(new Date(value));
};

const getNonEmptyValues = (
  values?: Array<string | null | undefined>,
): string[] =>
  (values ?? []).map((value) => trimValue(value)).filter(Boolean);

const joinSummaryParts = (...parts: Array<string | null | undefined>) =>
  parts.map((part) => trimValue(part)).filter(Boolean).join(" · ");

const getFieldFallback = (value?: React.ReactNode) =>
  value ? value : <span className="text-white/35">Not available</span>;

const ReviewAccordionCard: React.FC<ReviewAccordionCardProps> = ({
  title,
  summary,
  icon,
  sectionKey,
  status,
  isOpen,
  onToggle,
  actionSlot,
  children,
}) => {
  const styles = CARD_STYLES[sectionKey];

  return (
    <section
      className={`overflow-hidden rounded-[22px] border bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.025))] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ${styles.borderClassName}`}
    >
      <div
        className="flex cursor-pointer flex-wrap items-start justify-between gap-4 px-6 py-5"
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle();
          }
        }}
        aria-expanded={isOpen}
        aria-label={isOpen ? `Collapse ${title}` : `Expand ${title}`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-11 w-11 items-center justify-center rounded-xl ${styles.iconClassName}`}
            >
              {icon}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-[18px] font-semibold text-white">{title}</h2>
                {status === "complete" ? (
                  <CheckCircle2 className="h-[18px] w-[18px] text-emerald-400" />
                ) : (
                  <AlertCircle className="h-[18px] w-[18px] text-amber-400" />
                )}
              </div>
              <p className="mt-2 truncate text-sm text-white/45">{summary}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 text-sm text-white/55">
          {actionSlot}
          <span className="rounded-full p-1.5 text-white/70">
            <ChevronUp
              className={`h-5 w-5 transition-transform duration-200 ${
                isOpen ? "rotate-0" : "rotate-180"
              }`}
            />
          </span>
        </div>
      </div>

      {isOpen ? (
        <div className="border-t border-white/6 px-6 py-6">{children}</div>
      ) : null}
    </section>
  );
};

const ReviewField: React.FC<ReviewFieldProps> = ({
  label,
  value,
  span = "half",
}) => (
  <div className={span === "full" ? "md:col-span-2" : ""}>
    <p className="text-[13px] font-medium text-white/42">{label}</p>
    <div className="mt-2 text-[15px] leading-7 text-white/88">
      {getFieldFallback(value)}
    </div>
  </div>
);

const ReviewList = ({
  items,
  emptyLabel,
}: {
  items: string[];
  emptyLabel: string;
}) => {
  if (items.length === 0) {
    return <span className="text-white/35">{emptyLabel}</span>;
  }

  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex gap-3">
          <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-white/75" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

const LinkCard = ({
  title,
  subtitle,
  value,
  isCopied = false,
  onCopy,
  onOpen,
}: {
  title: string;
  subtitle: string;
  value: string;
  isCopied?: boolean;
  onCopy: () => void;
  onOpen: () => void;
}) => {
  const hasValue = Boolean(value);

  return (
    <div className="rounded-[22px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white">{title}</p>
          <p className="mt-1 text-xs leading-5 text-white/42">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCopy}
            disabled={!hasValue}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
              isCopied
                ? "border-emerald-400/30 bg-emerald-400/12 text-emerald-200"
                : "border-white/10 text-white/70 hover:border-white/20 hover:bg-white/5 hover:text-white"
            }`}
          >
            {isCopied ? (
              <CheckCircle2 className="h-3.5 w-3.5" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
            {isCopied ? "Copied" : "Copy"}
          </button>
          <button
            type="button"
            onClick={onOpen}
            disabled={!hasValue}
            className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/12 px-3 py-1.5 text-[11px] font-medium text-amber-200 transition-colors hover:bg-amber-400/18 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label={`Open ${title}`}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open
          </button>
        </div>
      </div>

      <div
        className={`mt-4 rounded-2xl border px-4 py-3 ${
          hasValue
            ? "border-amber-400/20 bg-amber-400/6"
            : "border-dashed border-white/12 bg-white/[0.03]"
        }`}
      >
        {hasValue ? (
          <code className="block overflow-hidden text-ellipsis whitespace-nowrap text-xs text-amber-200">
            {value}
          </code>
        ) : (
          <p className="text-xs leading-5 text-white/42">
            Link unavailable until the deployment URL is ready.
          </p>
        )}
      </div>
    </div>
  );
};

const getPrototypeQuestionRows = (questions: FeedbackFlowQuestion[]) => {
  const routeLabels = new Map<string, string>();
  let nextScreenNumber = 1;

  return questions.map((question, index) => {
    const route = trimValue(question.taggedTarget?.route);
    const fallbackKey = route || `untagged-${index}`;

    if (!routeLabels.has(fallbackKey)) {
      routeLabels.set(fallbackKey, `Screen ${nextScreenNumber}`);
      nextScreenNumber += 1;
    }

    return {
      id: question.id,
      label: routeLabels.get(fallbackKey) ?? `Screen ${index + 1}`,
      question: trimValue(question.question) || "Untitled question",
    };
  });
};

export const ExperimentFinalizeSection: React.FC<
  ExperimentFinalizeSectionProps
> = ({
  experimentData,
  goToStep,
  isFinalized,
  setIsFinalized,
}) => {
  const { experimentStatus, isExperimentLocked, saveAndExit, selectedExperiment } =
    useExperimentEditor();
  const isDemoExperiment = isDemoCreditCardExperimentId(
    experimentData.experimentId,
  );
  const demoOverrides = React.useMemo(
    () => (isDemoExperiment ? getCreditCardDemoFinalize() : null),
    [isDemoExperiment],
  );
  const [openSections, setOpenSections] = React.useState<
    Record<ReviewSectionKey, boolean>
  >({
    business: true,
    research: true,
    prototype: true,
  });
  const [populationSizeInput, setPopulationSizeInput] = React.useState(() =>
    String(demoOverrides?.populationSize ?? PARAMETER_DEFAULTS.populationSize),
  );
  const [targetResponsesInput, setTargetResponsesInput] = React.useState(() =>
    String(demoOverrides?.targetResponses ?? PARAMETER_DEFAULTS.targetResponses),
  );
  const [runStatus, setRunStatus] = React.useState<"not_started" | "running" | "paused">(
    "not_started",
  );
  const [finalizedAt, setFinalizedAt] = React.useState<string | null>(null);
  const [startedAt, setStartedAt] = React.useState<string | null>(null);
  const [liveResponsesTab, setLiveResponsesTab] = React.useState<
    "participants" | "demographics" | "interview"
  >("participants");
  const [isDeploymentStateHydrated, setIsDeploymentStateHydrated] =
    React.useState(false);
  const [copiedLinkKey, setCopiedLinkKey] = React.useState<string | null>(null);
  const copyFeedbackTimeoutRef = React.useRef<number | null>(null);
  const experimentId = trimValue(experimentData.experimentId) || "new";
  const effectiveIsFinalized = isFinalized || isExperimentLocked;
  const isCompletedExperiment = experimentStatus === "completed";
  const isRunDataVisible =
    runStatus === "running" || runStatus === "paused" || isCompletedExperiment;
  const canEditLiveParameters = !isExperimentLocked && runStatus !== "running";
  const deploymentHeadline = isCompletedExperiment
    ? "Experiment Completed"
    : runStatus === "running" || runStatus === "paused"
      ? "Experiment Deployed"
      : "Experiment Locked";
  const deploymentDescription = isCompletedExperiment
    ? "This experiment has ended and is now view only."
    : runStatus === "paused"
      ? "Your experiment is paused and can be resumed from here."
      : runStatus === "running"
        ? "Your experiment is live and collecting insights"
        : "Your experiment is locked and ready to launch.";
  const deploymentStateStorageKey = React.useMemo(
    () => buildDeploymentStateStorageKey(experimentId),
    [experimentId],
  );
  const internalPath = INNOVATION_ROUTES.PREVIEW_INTERNAL.replace(
    ":id",
    encodeURIComponent(experimentId),
  );
  const reportPath = `${INNOVATION_ROUTES.EXPERIMENT_REPORT}?id=${encodeURIComponent(
    experimentId,
  )}&name=${encodeURIComponent(trimValue(experimentData.name) || "Untitled Experiment")}`;
  const internalUrl = buildAppUrl(internalPath);
  const reportUrl = buildAppUrl(reportPath);

  const prototypeDetailQuery = usePrototypeByExperiment(
    experimentId !== "new" ? experimentId : undefined,
    {
      enabled: experimentId !== "new",
    },
  );
  const iterationId = React.useMemo(
    () => resolvePrototypeIterationIdFromDetail(prototypeDetailQuery.data),
    [prototypeDetailQuery.data],
  );

  const participantPreviewBaseUrl =
    getRuntimeConfig("VITE_PARTICIPANT_PREVIEW_URL");
  const participantUrl = buildExperimentParticipantPreviewUrl(
    experimentId,
    iterationId,
    participantPreviewBaseUrl,
  );
  const feedbackFlowQuery = useFeedbackFlow(
    experimentId !== "new" ? experimentId : undefined,
    iterationId,
    {
      enabled: experimentId !== "new" && Boolean(iterationId),
    },
  );
  const feedbackFlow =
    feedbackFlowQuery.data?.feedbackFlow ?? experimentData.feedbackFlow;

  const personalInfoQuestions = React.useMemo(
    () =>
      feedbackFlow ? getQuestionsBySection(feedbackFlow, "personal_info") : [],
    [feedbackFlow],
  );
  const demographicsQuestions = React.useMemo(
    () =>
      feedbackFlow ? getQuestionsBySection(feedbackFlow, "demographics") : [],
    [feedbackFlow],
  );
  const prototypeQuestions = React.useMemo(
    () => (feedbackFlow ? getQuestionsBySection(feedbackFlow, "prototype") : []),
    [feedbackFlow],
  );
  const taggedPrototypeQuestions = React.useMemo(
    () => prototypeQuestions.filter(isTaggedFeedbackQuestion),
    [prototypeQuestions],
  );
  const taggedRoutes = React.useMemo(
    () =>
      Array.from(
        new Set(
          taggedPrototypeQuestions
            .map((question) => trimValue(question.taggedTarget?.route))
            .filter(Boolean),
        ),
      ),
    [taggedPrototypeQuestions],
  );
  const prototypeQuestionRows = React.useMemo(
    () => getPrototypeQuestionRows(prototypeQuestions),
    [prototypeQuestions],
  );

  const experimentTypeLabel =
    EXPERIMENT_TYPE_LABELS[
      experimentData.experimentType ?? "validation"
    ] ?? "Validation";
  const coreResearchQuestions = getNonEmptyValues(
    experimentData.coreResearchQuestions,
  );
  const keyAssumptions = getNonEmptyValues(experimentData.keyAssumptions);
  const personalInfoQuestionLabels = personalInfoQuestions
    .map((question) => trimValue(question.question))
    .filter(Boolean);
  const demographicQuestionLabels = demographicsQuestions
    .map((question) => trimValue(question.question))
    .filter(Boolean);
  const researchCoverageCount =
    coreResearchQuestions.length > 0
      ? Math.min(taggedPrototypeQuestions.length, coreResearchQuestions.length)
      : 0;
  const prototypeSummary = joinSummaryParts(
    "No screens",
    `${taggedRoutes.length} tagged`,
    `${feedbackFlow?.questions.length ?? 0} questions`,
    `Welcome ${feedbackFlow?.intro.enabled ? "ON" : "OFF"}`,
    `Demographics ${feedbackFlow?.demographicsEnabled ? "ON" : "OFF"}`,
  );
  const prototypeDetailError = prototypeDetailQuery.error
    ? getExperimentApiErrorMessage(
        prototypeDetailQuery.error,
        "Unable to load prototype details.",
      )
    : null;
  const feedbackFlowError = feedbackFlowQuery.error
    ? getFeedbackFlowErrorMessage(
        feedbackFlowQuery.error,
        "Unable to load tagging details.",
      )
    : null;
  const prototypeNotice =
    prototypeDetailError ||
    feedbackFlowError ||
    (experimentId !== "new" &&
    !prototypeDetailQuery.isLoading &&
    !iterationId &&
    !feedbackFlow
      ? "Prototype and tagging details are not available yet."
      : null);

  React.useEffect(() => {
    setIsDeploymentStateHydrated(false);

    if (typeof window === "undefined") {
      setIsDeploymentStateHydrated(true);
      return;
    }

    try {
      const rawValue = window.localStorage.getItem(deploymentStateStorageKey);
      if (!rawValue) {
        setIsDeploymentStateHydrated(true);
        return;
      }

      const parsed = JSON.parse(rawValue) as Partial<{
        isFinalized: boolean;
        populationSizeInput: string;
        targetResponsesInput: string;
        runStatus: "not_started" | "running" | "paused";
        finalizedAt: string | null;
        startedAt: string | null;
        liveResponsesTab: "participants" | "demographics" | "interview";
      }>;

      if (typeof parsed.populationSizeInput === "string") {
        setPopulationSizeInput(parsed.populationSizeInput);
      }
      if (typeof parsed.targetResponsesInput === "string") {
        setTargetResponsesInput(parsed.targetResponsesInput);
      }
      if (parsed.runStatus === "not_started" || parsed.runStatus === "running" || parsed.runStatus === "paused") {
        setRunStatus(parsed.runStatus);
      }
      if (
        parsed.liveResponsesTab === "participants" ||
        parsed.liveResponsesTab === "demographics" ||
        parsed.liveResponsesTab === "interview"
      ) {
        setLiveResponsesTab(parsed.liveResponsesTab);
      }
      if (parsed.finalizedAt === null || typeof parsed.finalizedAt === "string") {
        setFinalizedAt(parsed.finalizedAt ?? null);
      }
      if (parsed.startedAt === null || typeof parsed.startedAt === "string") {
        setStartedAt(parsed.startedAt ?? null);
      }
      if (parsed.isFinalized === true) {
        setIsFinalized(true);
      }
    } catch {
      // Ignore malformed local state and continue with defaults.
    } finally {
      setIsDeploymentStateHydrated(true);
    }
  }, [deploymentStateStorageKey, setIsFinalized]);

  React.useEffect(() => {
    if (!isDeploymentStateHydrated || typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(
        deploymentStateStorageKey,
        JSON.stringify({
          isFinalized: effectiveIsFinalized,
          populationSizeInput,
          targetResponsesInput,
          runStatus,
          finalizedAt,
          startedAt,
          liveResponsesTab,
        }),
      );
    } catch {
      // Ignore local persistence failures.
    }
  }, [
    deploymentStateStorageKey,
    effectiveIsFinalized,
    finalizedAt,
    isDeploymentStateHydrated,
    liveResponsesTab,
    populationSizeInput,
    runStatus,
    startedAt,
    targetResponsesInput,
  ]);

  React.useEffect(() => {
    if (!isExperimentLocked) {
      return;
    }

    if (experimentStatus === "live") {
      setRunStatus("running");
      return;
    }

    if (experimentStatus === "paused") {
      setRunStatus("paused");
      return;
    }

    if (experimentStatus === "completed") {
      setRunStatus("not_started");
    }
  }, [experimentStatus, isExperimentLocked]);

  React.useEffect(() => {
    if (effectiveIsFinalized && !finalizedAt) {
      setFinalizedAt(
        selectedExperiment?.updatedAt ??
          selectedExperiment?.createdAt ??
          new Date().toISOString(),
      );
    }
  }, [
    effectiveIsFinalized,
    finalizedAt,
    selectedExperiment?.createdAt,
    selectedExperiment?.updatedAt,
  ]);

  const populationSize = populationSizeInput
    ? Number.parseInt(populationSizeInput, 10)
    : 0;
  const targetResponsesUnclamped = targetResponsesInput
    ? Number.parseInt(targetResponsesInput, 10)
    : PARAMETER_DEFAULTS.minimumResponses;
  const sliderMax = Math.max(
    PARAMETER_DEFAULTS.targetResponses,
    PARAMETER_DEFAULTS.minimumResponses,
    populationSize,
    targetResponsesUnclamped,
  );
  const targetResponses = Math.max(
    PARAMETER_DEFAULTS.minimumResponses,
    Math.min(targetResponsesUnclamped, sliderMax),
  );
  const sliderPercentage =
    sliderMax === PARAMETER_DEFAULTS.minimumResponses
      ? 100
      : ((targetResponses - PARAMETER_DEFAULTS.minimumResponses) /
          (sliderMax - PARAMETER_DEFAULTS.minimumResponses)) *
        100;
  const inviteRowsByColumn = RESPONSE_RATE_COLUMNS.map((column) =>
    column.map((rate) => {
      const invites = Math.ceil(targetResponses / (rate / 100));
      return {
        rate: `${rate}%`,
        invites,
        fitsPopulation: populationSize > 0 && invites <= populationSize,
      };
    }),
  );
  const currentResponses = isRunDataVisible
    ? demoOverrides?.currentResponses ??
      (isCompletedExperiment
        ? targetResponses
        : Math.min(
            targetResponses,
            Math.max(1, Math.round(targetResponses * 0.6)),
          ))
    : 0;
  const participantRows = demoOverrides?.participantRows ?? PARTICIPANT_RESPONSE_ROWS;
  const demographicRows = demoOverrides?.demographicRows ?? DEMOGRAPHIC_RESPONSE_ROWS;
  const responseProgress =
    targetResponses > 0 ? Math.round((currentResponses / targetResponses) * 100) : 0;

  const toggleSection = (sectionKey: ReviewSectionKey) => {
    setOpenSections((current) => ({
      ...current,
      [sectionKey]: !current[sectionKey],
    }));
  };

  const copyToClipboard = async (value: string) => {
    if (!value) {
      return;
    }

    await navigator.clipboard.writeText(value);
  };

  React.useEffect(() => {
    return () => {
      if (copyFeedbackTimeoutRef.current !== null) {
        window.clearTimeout(copyFeedbackTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyLink = async (copyKey: string, value: string) => {
    if (!value) {
      return;
    }

    await copyToClipboard(value);
    setCopiedLinkKey(copyKey);

    if (copyFeedbackTimeoutRef.current !== null) {
      window.clearTimeout(copyFeedbackTimeoutRef.current);
    }

    copyFeedbackTimeoutRef.current = window.setTimeout(() => {
      setCopiedLinkKey((current) => (current === copyKey ? null : current));
      copyFeedbackTimeoutRef.current = null;
    }, 1800);
  };

  const openPath = (path: string) => {
    if (!path) {
      return;
    }

    window.open(path, "_blank", "noopener,noreferrer");
  };

  const handlePopulationSizeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPopulationSizeInput(sanitizeIntegerInput(event.target.value));
  };

  const handlePopulationSizeBlur = () => {
    // Keep empty values empty so the control card can show its placeholder.
  };

  const handleTargetResponsesChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setTargetResponsesInput(sanitizeIntegerInput(event.target.value));
  };

  const handleTargetResponsesBlur = () => {
    if (!targetResponsesInput) {
      setTargetResponsesInput(String(PARAMETER_DEFAULTS.minimumResponses));
      return;
    }

    setTargetResponsesInput(String(targetResponses));
  };

  const { mutateAsync: updateStatus } = useUpdateExperimentStatus();

  const handleLockExperiment = () => {
    setFinalizedAt(new Date().toISOString());
    setStartedAt(null);
    setRunStatus("not_started");
    setLiveResponsesTab("participants");
    setIsFinalized(true);
  };

  const handleStartExperiment = async () => {
    if (experimentId !== "new") {
      await updateStatus({ experimentId, status: 3 });
    }
    setRunStatus("running");
    setStartedAt((current) => current ?? new Date().toISOString());
  };

  const handlePauseExperiment = async () => {
    if (experimentId !== "new") {
      await updateStatus({ experimentId, status: 4 });
    }
    setRunStatus("paused");
  };

  const handleEndExperiment = async () => {
    if (experimentId !== "new") {
      await updateStatus({ experimentId, status: 5 });
    }
    setRunStatus("not_started");
    setStartedAt(null);
    setLiveResponsesTab("participants");
  };

  const handleUnlockForEditing = () => {
    if (isExperimentLocked) {
      return;
    }

    setRunStatus("not_started");
    setStartedAt(null);
    setFinalizedAt(null);
    setLiveResponsesTab("participants");
    setIsFinalized(false);
  };

  if (effectiveIsFinalized) {
    return (
      <div className="flex-1 overflow-y-auto px-6 py-8 pb-20 md:px-12">
        <div className="mx-auto max-w-[1380px] space-y-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-7 w-2 rounded-full bg-amber-400" />
              <h1 className="text-[18px] font-semibold text-white md:text-[20px]">
                {deploymentHeadline}
              </h1>
            </div>
            <p className="mt-3 text-sm text-white/52">
              {deploymentDescription}
            </p>
          </div>

          <section className="rounded-[22px] border border-emerald-500/35 bg-[linear-gradient(180deg,rgba(7,54,24,0.96),rgba(9,46,21,0.96))] px-6 py-8 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <div className="flex flex-col items-start gap-5">
              <div className="flex items-start gap-4">
                <div className="mt-1 flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/12 text-emerald-300">
                  <Lock className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-[18px] font-semibold text-emerald-300">
                    {isCompletedExperiment
                      ? "Experiment completed & locked"
                      : "Experiment deployed & locked"}
                  </h2>
                  <p className="mt-1 text-sm text-emerald-100/70">
                    Locked on {formatDeploymentTimestamp(finalizedAt)}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={saveAndExit}
                className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/6 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Experiments
              </button>
            </div>
          </section>

          {runStatus !== "running" &&
          runStatus !== "paused" &&
          !isCompletedExperiment ? (
            <section className="rounded-[22px] border border-white/10 bg-white/[0.02] px-5 py-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
              <p className="text-base font-semibold text-white">What happens next</p>
              <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/58">
                <span className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                  Deployed via selected integration
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                  User interactions collected automatically
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                  Insights dashboard activates within 24h
                </span>
              </div>
            </section>
          ) : null}

          <section className="rounded-[22px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] px-5 py-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-amber-300" />
                <div className="flex items-center gap-3">
                  <h2 className="text-[18px] font-semibold text-white">
                    Experiment Control
                  </h2>
                  <span
                    className={`rounded-full border px-3 py-1 text-sm font-medium ${
                      isCompletedExperiment
                        ? "border-sky-500/35 bg-sky-500/12 text-sky-200"
                        : runStatus === "running"
                        ? "border-emerald-500/35 bg-emerald-500/12 text-emerald-300"
                        : runStatus === "paused"
                          ? "border-amber-500/35 bg-amber-500/12 text-amber-300"
                          : "border-white/14 bg-white/6 text-white/62"
                    }`}
                  >
                    {isCompletedExperiment
                      ? "Completed"
                      : runStatus === "running"
                        ? "Running"
                        : runStatus === "paused"
                          ? "Paused"
                          : "Not Started"}
                  </span>
                </div>
              </div>

              {isCompletedExperiment ? null : runStatus === "running" || runStatus === "paused" ? (
                <div className="flex items-center gap-3 self-start">
                  {runStatus === "running" ? (
                    <button
                      type="button"
                      onClick={() => void handlePauseExperiment()}
                      className="inline-flex items-center gap-2 rounded-xl border border-amber-500/35 bg-amber-500/10 px-4 py-3 text-sm font-medium text-amber-300 transition-colors hover:bg-amber-500/15"
                    >
                      <Pause className="h-4 w-4" />
                      Pause
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void handleStartExperiment()}
                      className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-400"
                    >
                      <Play className="h-4 w-4" />
                      Resume
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleEndExperiment()}
                    className="inline-flex items-center gap-2 rounded-xl border border-rose-500/35 bg-rose-500/10 px-4 py-3 text-sm font-medium text-rose-300 transition-colors hover:bg-rose-500/15"
                  >
                    <Square className="h-4 w-4" />
                    End
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => void handleStartExperiment()}
                  className="inline-flex items-center gap-2 self-start rounded-xl bg-emerald-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-400"
                >
                  <Play className="h-4 w-4" />
                  Start Experiment
                </button>
              )}
            </div>

            <div
              className={`mt-6 grid gap-4 ${
                isRunDataVisible
                  ? "xl:grid-cols-5"
                  : "xl:grid-cols-4"
              }`}
            >
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <p className="text-[13px] font-medium text-white/42">Population</p>
                {!canEditLiveParameters ? (
                  <p className="mt-2 text-[32px] font-semibold leading-none text-white">
                    {populationSize > 0 ? formatNumber(populationSize) : "—"}
                  </p>
                ) : (
                  <input
                    type="text"
                    inputMode="numeric"
                    value={populationSizeInput}
                    onChange={handlePopulationSizeChange}
                    onBlur={handlePopulationSizeBlur}
                    placeholder="e.g. 5000"
                    className="mt-2 w-full bg-transparent text-[32px] font-semibold leading-none text-white outline-none placeholder:text-white/30"
                    aria-label="Experiment control population"
                  />
                )}
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <p className="text-[13px] font-medium text-white/42">Confidence</p>
                <p className="mt-2 text-[32px] font-semibold leading-none text-white">
                  {PARAMETER_DEFAULTS.confidence}%
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-center gap-2 text-[13px] font-medium text-white/42">
                  <Users className="h-3.5 w-3.5" />
                  Responses
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[32px] font-semibold leading-none text-white">
                    {currentResponses}
                  </span>
                  <span className="text-[28px] text-white/28">/</span>
                  {!canEditLiveParameters ? (
                    <span className="text-[32px] font-semibold leading-none text-white">
                      {targetResponses}
                    </span>
                  ) : (
                    <input
                      type="text"
                      inputMode="numeric"
                      value={targetResponsesInput}
                      onChange={handleTargetResponsesChange}
                      onBlur={handleTargetResponsesBlur}
                      className="w-[88px] bg-transparent text-[32px] font-semibold leading-none text-white outline-none"
                      aria-label="Experiment control target responses"
                    />
                  )}
                  {canEditLiveParameters ? (
                    <PenTool className="h-4 w-4 text-white/35" />
                  ) : null}
                </div>
                <div className="mt-4 h-1.5 rounded-full bg-white/10">
                  <div
                    className="h-1.5 rounded-full bg-amber-400 transition-all duration-300"
                    style={{ width: `${responseProgress}%` }}
                  />
                </div>
                <p className="mt-3 text-sm text-white/45">
                  {responseProgress}% of target
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-center gap-2 text-[13px] font-medium text-white/42">
                  <Clock3 className="h-3.5 w-3.5" />
                  Duration
                </div>
                {runStatus === "running" ? (
                  <>
                    <p className="mt-2 text-[32px] font-semibold leading-none text-white">
                      ~1d left
                    </p>
                    <p className="mt-3 text-sm text-white/42">48.0/day · Day 1</p>
                    <p className="mt-2 text-sm text-white/42">
                      Started {formatShortStartedDate(startedAt ?? finalizedAt)}
                    </p>
                  </>
                ) : runStatus === "paused" ? (
                  <>
                    <p className="mt-2 text-[32px] font-semibold leading-none text-white">
                      Paused
                    </p>
                    <p className="mt-3 text-sm text-white/42">
                      Started {formatShortStartedDate(startedAt ?? finalizedAt)}
                    </p>
                    <p className="mt-2 text-sm text-white/42">
                      Resume whenever you&apos;re ready.
                    </p>
                  </>
                ) : isCompletedExperiment ? (
                  <>
                    <p className="mt-2 text-[32px] font-semibold leading-none text-white">
                      Complete
                    </p>
                    <p className="mt-3 text-sm text-white/42">
                      Final state: {formatExperimentStatusLabel(experimentStatus)}
                    </p>
                  </>
                ) : (
                  <p className="mt-2 text-[32px] font-semibold leading-none text-white">
                    —
                  </p>
                )}
              </div>

              {isRunDataVisible ? (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="flex items-center gap-2 text-[13px] font-medium text-white/42">
                    <Timer className="h-3.5 w-3.5" />
                    Avg. Completion Time
                  </div>
                  <p className="mt-2 text-[32px] font-semibold leading-none text-white">
                    {demoOverrides?.avgCompletionTime ?? "4.2m"}
                  </p>
                  <p className="mt-3 text-sm text-white/42">per participant</p>
                </div>
              ) : null}
            </div>

            <div className="mt-5 border-t border-white/8 pt-5">
              <p className="text-base font-semibold text-white">Links</p>
              <p className="mt-1 text-sm text-white/42">
                Share the participant route or open the internal demo for QA.
              </p>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <LinkCard
                  title="Participant link"
                  subtitle="Share with test participants"
                  value={participantUrl}
                  isCopied={copiedLinkKey === "summary-participant"}
                  onCopy={() => void handleCopyLink("summary-participant", participantUrl)}
                  onOpen={() => openPath(participantUrl)}
                />
                <LinkCard
                  title="Demo link"
                  subtitle="Use for internal review and walkthroughs"
                  value={internalUrl}
                  isCopied={copiedLinkKey === "summary-demo"}
                  onCopy={() => void handleCopyLink("summary-demo", internalUrl)}
                  onOpen={() => openPath(internalUrl)}
                />
              </div>
            </div>
          </section>

          <section className="rounded-[22px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] px-5 py-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <MessageSquareText className="h-5 w-5 text-amber-300" />
                <h2 className="text-[18px] font-semibold text-white">
                  Live Responses
                </h2>
              </div>
              {isRunDataVisible ? (
                <span className="text-sm text-white/42">
                  {participantRows.length} participants
                </span>
              ) : null}
            </div>

            {!isRunDataVisible ? (
              <div className="flex min-h-[220px] items-center justify-center text-center text-lg text-white/38">
                Responses will appear here once the experiment is running.
              </div>
            ) : (
              <>
                <div className="mt-5 flex rounded-xl border border-white/10 bg-white/[0.03] p-1">
                  {[
                    {
                      key: "participants" as const,
                      label: "Participants",
                      count: participantRows.length,
                    },
                    {
                      key: "demographics" as const,
                      label: "Demographics",
                      count: demographicRows.length,
                    },
                    {
                      key: "interview" as const,
                      label: "Interview",
                      count: INTERVIEW_RESPONSE_ROWS.length,
                    },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setLiveResponsesTab(tab.key)}
                      className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors ${
                        liveResponsesTab === tab.key
                          ? "bg-white/14 text-white"
                          : "text-white/42 hover:text-white/70"
                      }`}
                    >
                      <span className="inline-flex items-center gap-2">
                        {tab.key === "participants" ? (
                          <Users className="h-4 w-4" />
                        ) : tab.key === "demographics" ? (
                          <FileText className="h-4 w-4" />
                        ) : (
                          <MessageSquareText className="h-4 w-4" />
                        )}
                        {tab.label}
                        <span className="text-xs opacity-70">({tab.count})</span>
                      </span>
                    </button>
                  ))}
                </div>

                <div className="mt-5 overflow-x-auto">
                  {liveResponsesTab === "participants" ? (
                    <table className="min-w-full border-separate border-spacing-y-0 text-left">
                      <thead>
                        <tr className="text-sm text-white/42">
                          <th className="border-b border-white/8 px-4 py-3 font-medium">#</th>
                          <th className="border-b border-white/8 px-4 py-3 font-medium">First Name</th>
                          <th className="border-b border-white/8 px-4 py-3 font-medium">Last Name</th>
                          <th className="border-b border-white/8 px-4 py-3 font-medium">Email</th>
                          <th className="border-b border-white/8 px-4 py-3 font-medium">Referral</th>
                          <th className="border-b border-white/8 px-4 py-3 text-right font-medium">Date Submitted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {participantRows.map((row) => (
                          <tr key={row.id} className="text-[15px] text-white/78">
                            <td className="border-b border-white/6 px-4 py-4">{row.id}</td>
                            <td className="border-b border-white/6 px-4 py-4">{row.firstName}</td>
                            <td className="border-b border-white/6 px-4 py-4">{row.lastName}</td>
                            <td className="border-b border-white/6 px-4 py-4">{row.email}</td>
                            <td className="border-b border-white/6 px-4 py-4">{row.referral}</td>
                            <td className="border-b border-white/6 px-4 py-4 text-right text-white/55">
                              {row.dateSubmitted}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : liveResponsesTab === "demographics" ? (
                    <table className="min-w-full text-left">
                      <thead>
                        <tr className="text-sm text-white/42">
                          <th className="border-b border-white/8 px-4 py-3 font-medium">Question</th>
                          <th className="border-b border-white/8 px-4 py-3 font-medium">Leading Answer</th>
                          <th className="border-b border-white/8 px-4 py-3 text-right font-medium">Completion</th>
                        </tr>
                      </thead>
                      <tbody>
                        {demographicRows.map((row) => (
                          <tr key={row.id} className="text-[15px] text-white/78">
                            <td className="border-b border-white/6 px-4 py-4">{row.question}</td>
                            <td className="border-b border-white/6 px-4 py-4">{row.leadingAnswer}</td>
                            <td className="border-b border-white/6 px-4 py-4 text-right text-white/55">
                              {row.completion}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <table className="min-w-full text-left">
                      <thead>
                        <tr className="text-sm text-white/42">
                          <th className="border-b border-white/8 px-4 py-3 font-medium">Participant</th>
                          <th className="border-b border-white/8 px-4 py-3 font-medium">Screen</th>
                          <th className="border-b border-white/8 px-4 py-3 font-medium">Question</th>
                          <th className="border-b border-white/8 px-4 py-3 font-medium">Answer</th>
                        </tr>
                      </thead>
                      <tbody>
                        {INTERVIEW_RESPONSE_ROWS.map((row) => (
                          <tr key={row.id} className="text-[15px] text-white/78">
                            <td className="border-b border-white/6 px-4 py-4">{row.participant}</td>
                            <td className="border-b border-white/6 px-4 py-4">{row.screen}</td>
                            <td className="border-b border-white/6 px-4 py-4">{row.question}</td>
                            <td className="border-b border-white/6 px-4 py-4 text-white/62">{row.answer}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
          </section>

          {!isExperimentLocked && runStatus !== "running" ? (
            <div className="flex justify-center pt-1">
              <button
                type="button"
                onClick={handleUnlockForEditing}
                className="inline-flex items-center gap-2 text-sm text-white/42 transition-colors hover:text-white/72"
              >
                <Lock className="h-4 w-4" />
                Unlock for editing
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-6 py-8 pb-20 md:px-12">
      <div className="mx-auto max-w-[1500px] space-y-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="h-7 w-2 rounded-full bg-amber-400" />
              <div>
                <h1 className="text-[18px] font-semibold text-white md:text-[20px]">
                  Review & Deploy
                </h1>
                <p className="mt-2 text-sm text-white/52">
                  Review your experiment setup before finalizing
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 self-start">
            {effectiveIsFinalized && !isExperimentLocked ? (
              <button
                type="button"
                onClick={() => setIsFinalized(false)}
                className="rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
              >
                Unlock
              </button>
            ) : null}
          </div>
        </div>

        {effectiveIsFinalized ? (
          <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/8 px-5 py-4 text-sm text-emerald-200">
            Experiment locked. Deployment links are ready to share below.
          </div>
        ) : null}

        <ReviewAccordionCard
          title="Business Context"
          summary={joinSummaryParts(experimentData.name, experimentData.goal)}
          icon={<FileText className="h-5 w-5" />}
          sectionKey="business"
          status="complete"
          isOpen={openSections.business}
          onToggle={() => toggleSection("business")}
          actionSlot={
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                goToStep(1);
              }}
              className="text-sm font-medium text-white/65 transition-colors hover:text-white"
            >
              Edit
            </button>
          }
        >
          <div className="grid gap-8 md:grid-cols-2">
            <ReviewField
              label="Experiment name"
              value={trimValue(experimentData.name)}
            />
            <ReviewField
              label="Business goal"
              value={trimValue(experimentData.goal)}
            />
            <ReviewField
              label="Target audience"
              value={trimValue(experimentData.targetAudience)}
              span="full"
            />
            <ReviewField
              label="Context"
              value={trimValue(experimentData.description)}
              span="full"
            />
          </div>
        </ReviewAccordionCard>

        <ReviewAccordionCard
          title="Research Foundation"
          summary={joinSummaryParts(
            experimentTypeLabel,
            experimentData.researchObjective || experimentData.goal,
          )}
          icon={<FlaskConical className="h-5 w-5" />}
          sectionKey="research"
          status="complete"
          isOpen={openSections.research}
          onToggle={() => toggleSection("research")}
          actionSlot={
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                goToStep(2);
              }}
              className="text-sm font-medium text-white/65 transition-colors hover:text-white"
            >
              Edit
            </button>
          }
        >
          <div className="grid gap-8 md:grid-cols-2">
            <ReviewField label="Experiment type" value={experimentTypeLabel} />
            <ReviewField
              label="Research objective"
              value={trimValue(experimentData.researchObjective)}
            />
            <ReviewField
              label="Core research questions"
              value={
                <ReviewList
                  items={coreResearchQuestions}
                  emptyLabel="No research questions yet"
                />
              }
              span="full"
            />
            <ReviewField
              label="Primary hypothesis"
              value={trimValue(experimentData.primaryHypothesis)}
              span="full"
            />
            <ReviewField
              label="Key assumptions"
              value={
                <ReviewList
                  items={keyAssumptions}
                  emptyLabel="No assumptions captured"
                />
              }
              span="full"
            />
            <ReviewField
              label="Success signal (qualitative)"
              value={trimValue(experimentData.successSignals?.qualitative)}
            />
            <ReviewField
              label="Success signal (quantitative)"
              value={trimValue(experimentData.successSignals?.quantitative)}
            />
          </div>
        </ReviewAccordionCard>

        <ReviewAccordionCard
          title="Prototype & Tags"
          summary={prototypeSummary}
          icon={<Layers3 className="h-5 w-5" />}
          sectionKey="prototype"
          status="attention"
          isOpen={openSections.prototype}
          onToggle={() => toggleSection("prototype")}
          actionSlot={
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  openPath(internalUrl);
                }}
                className="inline-flex items-center gap-1.5 text-white/70 transition-colors hover:text-white"
                disabled={experimentId === "new"}
              >
                <Eye className="h-4 w-4" />
                Preview
              </button>
              <span className="text-white/20">|</span>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  goToStep(3);
                }}
                className="font-medium text-white/70 transition-colors hover:text-white"
              >
                Edit Prototype
              </button>
              <span className="text-white/20">·</span>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  goToStep(4);
                }}
                className="font-medium text-white/70 transition-colors hover:text-white"
              >
                Edit Tags
              </button>
            </div>
          }
        >
          <div className="grid gap-8 md:grid-cols-2">
            {prototypeNotice ? (
              <div className="rounded-2xl border border-amber-500/20 bg-amber-500/8 px-4 py-3 text-sm text-amber-100 md:col-span-2">
                {prototypeNotice}
              </div>
            ) : null}

            <ReviewField label="Screens" value="No screens" />
            <ReviewField
              label="Idea"
              value={joinSummaryParts(
                trimValue(experimentData.name),
                trimValue(experimentData.targetAudience)
                  ? `targeting ${trimValue(experimentData.targetAudience)}`
                  : "",
              )}
            />
            <ReviewField
              label="Front matter"
              value={feedbackFlow?.intro.enabled ? "Enabled" : "Disabled"}
            />
            <ReviewField
              label="Demographics"
              value={feedbackFlow?.demographicsEnabled ? "Enabled" : "Disabled"}
            />
            <ReviewField
              label="Tagged screens"
              value={`${taggedRoutes.length} screen${
                taggedRoutes.length === 1 ? "" : "s"
              }`}
            />
            <ReviewField
              label="Total questions"
              value={`${feedbackFlow?.questions.length ?? 0} question${
                (feedbackFlow?.questions.length ?? 0) === 1 ? "" : "s"
              }`}
            />
            <ReviewField
              label="Research question coverage"
              value={
                coreResearchQuestions.length > 0
                  ? `${researchCoverageCount}/${coreResearchQuestions.length} research questions covered`
                  : "No research questions defined"
              }
            />
            <div />
            <ReviewField
              label="Participant Info Questions"
              value={
                <ReviewList
                  items={personalInfoQuestionLabels}
                  emptyLabel="No participant info questions"
                />
              }
              span="full"
            />
            <ReviewField
              label="Demographic Questions"
              value={
                <ReviewList
                  items={demographicQuestionLabels}
                  emptyLabel="No demographic questions"
                />
              }
              span="full"
            />
            <ReviewField
              label="Interview Questions (per screen)"
              value={
                prototypeQuestionRows.length > 0 ? (
                  <ul className="space-y-2">
                    {prototypeQuestionRows.map((row) => (
                      <li key={row.id} className="flex gap-3">
                        <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-white/75" />
                        <span>
                          {row.label}: {row.question}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="text-white/35">
                    No prototype interview questions
                  </span>
                )
              }
              span="full"
            />
          </div>
        </ReviewAccordionCard>

        <section className="rounded-[22px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] px-5 py-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/6 text-white">
              <FlaskConical className="h-[18px] w-[18px]" />
            </div>
            <h2 className="text-[18px] font-semibold text-white">
              Experiment Parameters
            </h2>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.1fr_1.2fr_1fr]">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <p className="text-lg font-semibold text-white">Population size</p>
              <p className="mt-2 text-xs text-white/42">
                Total number in your target audience for this experiment
              </p>
              <input
                type="text"
                inputMode="numeric"
                value={populationSizeInput}
                onChange={handlePopulationSizeChange}
                onBlur={handlePopulationSizeBlur}
                onFocus={(event) => event.target.select()}
                className="mt-4 w-full rounded-xl border border-white/12 bg-white/[0.04] px-4 py-3 text-[30px] font-medium tracking-tight text-white outline-none transition-colors placeholder:text-white/20 focus:border-white/25"
                aria-label="Population size"
              />
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <p className="text-lg font-semibold text-white">Target responses</p>
              <div className="mt-3 grid grid-cols-3 items-end text-sm">
                <span className="text-white/42">
                  {PARAMETER_DEFAULTS.minimumResponses}
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={targetResponsesInput}
                  onChange={handleTargetResponsesChange}
                  onBlur={handleTargetResponsesBlur}
                  onFocus={(event) => event.target.select()}
                  className="w-full bg-transparent text-center text-[30px] font-semibold leading-none text-white outline-none placeholder:text-white/20"
                  aria-label="Target responses"
                />
                <span className="text-right text-white/42">
                  {sliderMax}
                </span>
              </div>
              <div className="relative mt-3">
                <div className="h-3 rounded-full bg-[#4b3a11]" />
                <div
                  className="absolute left-0 top-0 h-3 rounded-full bg-[#f4c145]"
                  style={{ width: `${sliderPercentage}%` }}
                />
                <div
                  className="absolute top-1/2 h-7 w-7 -translate-y-1/2 rounded-full border-2 border-[#f4c145] bg-[#121212] shadow-[0_0_20px_rgba(244,193,69,0.4)]"
                  style={{
                    left: `clamp(0px, calc(${sliderPercentage}% - 14px), calc(100% - 28px))`,
                  }}
                />
                <input
                  type="range"
                  min={PARAMETER_DEFAULTS.minimumResponses}
                  max={sliderMax}
                  value={targetResponses}
                  onChange={(event) => setTargetResponsesInput(event.target.value)}
                  className="absolute inset-0 h-7 w-full cursor-pointer opacity-0"
                  aria-label="Target responses slider"
                />
              </div>

              <div className="group relative mt-4 w-max">
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-white"
                >
                  <Info className="h-3.5 w-3.5 text-white/60" />
                  Invites by response rate
                </button>
                <div className="pointer-events-none absolute left-0 top-full z-20 mt-3 w-[300px] rounded-2xl border border-white/12 bg-[#171717] p-4 opacity-0 shadow-2xl transition-all duration-150 group-hover:opacity-100">
                  <p className="text-xs font-semibold text-white/45">
                    Invites needed for {targetResponses} responses
                  </p>
                  <div className="mt-3 grid grid-cols-2 gap-6 text-sm">
                    {inviteRowsByColumn.map((column, columnIndex) => (
                      <div key={columnIndex} className="space-y-2">
                        {column.map((row) => (
                          <div
                            key={row.rate}
                            className="grid grid-cols-[auto_1fr] items-center gap-3"
                          >
                            <span
                              className={
                                row.fitsPopulation
                                  ? "text-emerald-200"
                                  : "text-rose-200"
                              }
                            >
                              {row.rate}
                            </span>
                            <span
                              className={`text-right font-medium ${
                                row.fitsPopulation
                                  ? "text-emerald-300"
                                  : "text-rose-300"
                              }`}
                            >
                              {formatNumber(row.invites)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/42">
                Confidence
              </p>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-[34px] font-semibold leading-none text-white">
                  {PARAMETER_DEFAULTS.confidence}%
                </span>
                <span className="inline-flex items-center gap-1 text-sm text-white/42">
                  <Lock className="h-3.5 w-3.5" />
                  Fixed
                </span>
              </div>

              <div className="mt-6">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/42">
                  Duration
                </p>
                <p className="mt-3 text-sm text-white/55">
                  Ends when target reached or manually ended.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-[22px] border border-amber-500/30 bg-[linear-gradient(180deg,rgba(244,193,69,0.06),rgba(244,193,69,0.03))] px-6 py-10 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
          <div className="mx-auto flex w-fit items-center gap-3 text-[18px] font-semibold text-amber-300">
            <Lock className="h-5 w-5" />
            {effectiveIsFinalized ? "Experiment locked" : "Ready to deploy?"}
          </div>
          <p className="mx-auto mt-5 max-w-3xl text-[15px] leading-8 text-white/78">
            Once locked, the experiment configuration cannot be edited. Deployment
            links will be generated automatically.
          </p>
          <p className="mt-3 text-[15px] text-amber-300/90">
            Your insights dashboard will begin populating with real-time data
            immediately after deployment.
          </p>

          {!effectiveIsFinalized ? (
            <button
              type="button"
              onClick={handleLockExperiment}
              className="mt-8 inline-flex items-center gap-3 rounded-xl bg-[#f4c145] px-7 py-3.5 text-base font-semibold text-black transition-transform hover:scale-[1.01]"
            >
              <Lock className="h-[18px] w-[18px]" />
              Lock Experiment
            </button>
          ) : null}
        </section>

        {effectiveIsFinalized ? (
          <section className="rounded-[22px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <div className="mb-5 flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-400/12 text-amber-300">
                <Tag className="h-[18px] w-[18px]" />
              </div>
              <div>
                <h2 className="text-[18px] font-semibold text-white">
                  Deployment Links
                </h2>
                <p className="mt-1 text-sm text-white/42">
                  Share the participant experience and internal review routes.
                </p>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              <LinkCard
                title="Participant link"
                subtitle="Share with test participants"
                value={participantUrl}
                isCopied={copiedLinkKey === "deployment-participant"}
                onCopy={() =>
                  void handleCopyLink("deployment-participant", participantUrl)
                }
                onOpen={() => openPath(participantUrl)}
              />
              <LinkCard
                title="Internal link"
                subtitle="For team review and QA"
                value={internalUrl}
                isCopied={copiedLinkKey === "deployment-internal"}
                onCopy={() =>
                  void handleCopyLink("deployment-internal", internalUrl)
                }
                onOpen={() => openPath(internalUrl)}
              />
              <LinkCard
                title="Insights report"
                subtitle="Share the finalized experiment summary"
                value={reportUrl}
                isCopied={copiedLinkKey === "deployment-report"}
                onCopy={() =>
                  void handleCopyLink("deployment-report", reportUrl)
                }
                onOpen={() => openPath(reportUrl)}
              />
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
};
